import React, { useEffect, useState } from "react";

import { API } from "aws-amplify";
import {
  Flex,
  Table,
  TableCell,
  TableBody,
  TableRow,
  SelectField,
  Badge,
  Loader,
  Heading,
  Button,
  Icon,
} from "@aws-amplify/ui-react";
import { usePapaParse } from "react-papaparse";

import OrderLineItem from "./OrderLineItem";

function OrdersReport() {
  const [orderList, setOrders] = useState<any[]>([]);
  const [pageNo, setPageNo] = useState<number>(1);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [selectedStatus, setStatus] = useState<string>("processing");
  const { jsonToCSV } = usePapaParse();

  const IconSave = () => {
    return (
      <Icon
        ariaLabel=""
        pathData="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3ZM19 19H5V5H16.17L19 7.83V19ZM12 12C10.34 12 9 13.34 9 15C9 16.66 10.34 18 12 18C13.66 18 15 16.66 15 15C15 13.34 13.66 12 12 12ZM6 6H15V10H6V6Z"
      />
    );
  };

  const loadOrders = () => {
    if (!isLoading) {
      setLoading(true);
      API.get(
        "adminApi",
        `/rest/orders_report?status=${selectedStatus}&page_no=${pageNo}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      )
        .then((data) => {
          console.log(data);
          if (data?.orders && data?.orders.length > 0) {
            if (pageNo === 1) {
              setOrders(data.orders);
            } else {
              setOrders((orders) => [...orders, ...data.orders]);
            }
            setPageNo(pageNo + 1);
          }

          setLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    loadOrders();
  }, [pageNo]);

  const addressLine = (address: any) => {
    let address_line = address.postcode;
    if (address.street && address.street.length > 0) {
      address_line = `${address.street[0]},\n`;
    }
    if (address.postcode) {
      address_line += `${address.postcode},`;
    }
    return address_line;
  };

  const prepareItemLine = (item: any, csvItem: any) => {
    const skuArr: string[] = item.variant_sku.split("-");
    if (csvItem.order_id === "2000000195") {
      console.log(item.barcode);
    }
    const newcsvItem = csvItem;
    newcsvItem["sku"] = skuArr[0];
    if (skuArr.length > 1) {
      newcsvItem["variant"] = skuArr[1];
    }
    newcsvItem["barcode"] = item.barcode;
    newcsvItem["product_name"] = item.product_name;
    newcsvItem["ordered_qty"] = item.ordered_qty;
    const product_name_arr: string[] = item.product_name.split("-");
    if (product_name_arr.length > 2) {
      newcsvItem["product_name"] = product_name_arr[0];
      newcsvItem["color, size"] = `${
        product_name_arr[product_name_arr.length - 2]
      }, ${product_name_arr[product_name_arr.length - 1]}`;
    } else {
      newcsvItem["color, size"] = "";
    }
    if (item.inventory) {
      Object.keys(item.inventory).forEach((stock_key) => {
        if (stock_key !== "Id") {
          newcsvItem[`${stock_key.replace("_qty", "")}`] =
            item.inventory[stock_key];
        }
      });
    }
    return newcsvItem;
  };

  const prepareOrderPart = (order: any) => {
    return {
      order_id: order.order_id,
      order_date: order.order_date,
      name: order.customer_name,
      "Heimilisfang m póstnúmer": addressLine(order.shipping_address),
      phone: order.shipping_address?.telephone,
      shipping: `${formatShipDescription(order?.shipping_description)}\n${
        order.comment ? shipLocation(order?.comment) : ""
      }`,
    };
  };

  const prepareCsvSupportReport = () => {
    let csvReportJson: any = [];
    orderList.forEach((order) => {
      const orderItems: any[] = order.order_items;
      if (orderItems && orderItems.length > 0) {
        csvReportJson = [
          ...csvReportJson,
          ...orderItems.map((item: any, i: number) =>
            prepareItemLine(item, prepareOrderPart(order))
          ),
        ];
      } else {
        csvReportJson.push(prepareOrderPart(order));
      }
    });
    return csvReportJson;
  };

  const prepareCsv = async () => {
    const csvReportJson = prepareCsvSupportReport();
    const csv = jsonToCSV(csvReportJson, {
      columns: [
        "order_id",
        "order_date",
        "shipping",
        "sku",
        "variant",
        "product_name",
        "ordered_qty",
        "color, size",
        "barcode",
        "D11",
        "D02",
        "D07",
        "D03",
        "name",
        "Heimilisfang m póstnúmer",
        "phone",
      ],
    });
    console.log(csv);

    // You can save it as a CSV file using Blob and createObjectURL
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const toDate = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.setAttribute("download", `processing_${toDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatShipDescription = (description: string) => {
    if (description !== "") {
      return description.split("-")[0];
    }
    return description;
  };

  const shipLocation = (comment: string) => {
    const rawText = comment?.replace("Data: Payload ", "");
    if (!rawText) {
      return "";
    }
    let data: any = {};
    try {
      data = JSON.parse(rawText);
    } catch (error) {
      data["shippingMethodCode"] = rawText;
    }
    let locationInfo = rawText;
    if (data?.shippingMethodCode) {
      locationInfo = data.shippingMethodCode;
    }
    if (data?.shippingDetails && data.shippingDetails !== "") {
      locationInfo += `: ${data.shippingDetails}`;
    }
    return locationInfo;
  };

  const orderLine = (order: any) => {
    return (
      <Flex
        direction="row"
        justifyContent="space-between"
        alignItems="stretch"
        alignContent="space-between"
        wrap="nowrap"
        gap="1rem"
        style={{ width: "100%" }}
      >
        <span>{order.order_id}</span>
        <span>{order.customer_name}</span>
        <small>{new Date(order.order_date).toUTCString()}</small>
        <span>
          {formatShipDescription(order?.shipping_description)}
          <Badge size="small" variation="success">
            {shipLocation(order?.comment)}
          </Badge>
        </span>
      </Flex>
    );
  };

  const changeStatus = (e: any) => {
    setStatus(e.target.value);
    setOrders([]);
    setPageNo(1);
  };
  return (
    <>
      <Flex
        direction="row"
        justifyContent="space-between"
        alignItems="stretch"
        alignContent="space-between"
        wrap="nowrap"
        gap="1rem"
        style={{ marginBottom: "1rem" }}
      >
        <Heading level={4}>
          Orders
          <Badge size="small" variation="error">
            {selectedStatus}
          </Badge>
        </Heading>
        <Button
          className="hide-on-print"
          onClick={() => window.print()}
          disabled={isLoading}
          gap="0.1rem"
          size="small"
        >
          <IconSave /> Print
        </Button>
        <Button
          className="hide-on-print"
          disabled={isLoading}
          onClick={() => prepareCsv()}
          gap="0.1rem"
          size="small"
        >
          <IconSave /> Download CSV
        </Button>
        <SelectField
          className="hide-on-print"
          label=""
          onChange={(e) => changeStatus(e)}
          value="processing"
          disabled={isLoading}
        >
          <option value="processing">Processing</option>
          <option value="pending_payment">Pending Payment</option>
          <option value="draft" disabled>
            Draft
          </option>
          <option value="complete">Success</option>
        </SelectField>
      </Flex>
      {isLoading && (
        <>
          Loading...
          <Loader />
        </>
      )}
      {orderList && orderList.length > 0 ? (
        <Table variation="striped" size="small">
          <TableBody>
            {orderList.map((order) => (
              <>
                <TableRow key={order.order_id}>
                  <TableCell colSpan={7}>{orderLine(order)}</TableCell>
                </TableRow>
                {order.order_items.length > 0 &&
                  order.order_items.map((item: any, index: number) => (
                    <OrderLineItem
                      key={`${order.order_id}_${index}`}
                      item={item}
                    />
                  ))}
              </>
            ))}
          </TableBody>
        </Table>
      ) : (
        <p>No Data Available!</p>
      )}
    </>
  );
}

export default OrdersReport;
